import type { CreateAccountingOperationPayload, ServiceAccountingOperationType } from '@shared/api/contracts';

export interface CreateOperationFormValues {
  sum: string;
  walletId: string;
  invoiceId: string;
  comment: string;
  type: ServiceAccountingOperationType;
  assetLabel: string;
  relatedOperationIds: string;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SIGNED_DECIMAL = /^[-+]?(?:\d+\.?\d*|\.\d+)$/;
const ZERO_DECIMAL = /^[-+]?0+(?:\.0+)?$/;

export const normalizeSignedAmountInput = (raw: string): string =>
  raw
    .trim()
    .replace(/[\u2212\u2013\u2014]/g, '-')
    .replace(/\s+/g, '')
    .replace(',', '.')
    .replace(/^\+/, '');

export const parseRelatedOperationIds = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(/[,;\s]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ).slice(0, 100);

export const isNonZeroSignedDecimal = (raw: string): boolean => {
  const text = normalizeSignedAmountInput(raw);
  return SIGNED_DECIMAL.test(text) && !ZERO_DECIMAL.test(text);
};

export const isOptionalInvoiceId = (raw: string): boolean => {
  const invoiceId = raw.trim();
  return invoiceId.length === 0 || UUID_PATTERN.test(invoiceId);
};

export const toCreateAccountingOperationPayload = (
  values: CreateOperationFormValues,
): CreateAccountingOperationPayload => {
  const sum = normalizeSignedAmountInput(values.sum);
  const invoiceId = values.invoiceId.trim();
  const assetLabel = values.assetLabel.trim();
  const relatedOperationIds = parseRelatedOperationIds(values.relatedOperationIds);

  return {
    sum,
    walletId: Number(values.walletId),
    comment: values.comment.trim(),
    type: values.type,
    ...(invoiceId ? { invoiceId } : {}),
    ...(assetLabel ? { assetLabel } : {}),
    ...(relatedOperationIds.length ? { relatedOperationIds } : {}),
  };
};
